import React, { memo, useCallback } from 'react';

import { Platform, TouchableOpacity, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { DragIcon, EyeCrossedIcon, EyeIcon } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { RBChannel, RBDeviceType } from 'store/railsBuilder/reducer.types';

import { ImageSize } from 'components/NagraImage/types';
import ChannelCard from 'components/Ribbon/components/ChannelCard/ChannelCard';

import styles from './styles';

export type ChannelsContent = {
    editorial: {
        id: string;
        longName?: string;
    };
    hidden: boolean;
    visible: boolean;
    isSubscribed?: boolean;
};

type Props = {
    item: ChannelsContent;
    index?: number;
    onLongPress: () => void;
    toggleItemVisibility: (index: number) => void;
    screenReaderEnabled?: boolean;
};

const {
    ACCESSIBILITY_MY_LIST_HIDE_CHANNEL,
    ACCESSIBILITY_MY_LIST_SHOW_CHANNEL,
} = LANGUAGE_KEYS.ACCESSIBILITY.SETTINGS.CHANNEL_LINEUP;

const ChannelLineupItem = memo(
    ({
        item,
        index,
        onLongPress,
        toggleItemVisibility,
        screenReaderEnabled,
    }: Props) => {
        const { t } = useTranslation();
        const updateItemVisibility = useCallback(() => {
            if (typeof index === 'number') {
                toggleItemVisibility(index);
            }
        }, [toggleItemVisibility, index]);

        return (
            <View style={styles.container}>
                <TouchableOpacity
                    onLongPress={onLongPress}
                    delayLongPress={100}
                    disabled={screenReaderEnabled}
                    importantForAccessibility="no"
                    accessibilityElementsHidden
                    focusable={false}
                >
                    <DragIcon />
                </TouchableOpacity>
                <ChannelCard
                    item={item as unknown as Partial<RBChannel>}
                    style={styles.channelContainer}
                    logoStyle={styles.logo}
                    width={ImageSize.SETTINGS_LIST_CHANNEL_LOGO.width}
                    height={ImageSize.SETTINGS_LIST_CHANNEL_LOGO.height}
                    isTitleVisible
                    isOnSettingsPage
                />

                <TouchableOpacity
                    onPress={updateItemVisibility}
                    style={styles.icon}
                    accessibilityRole={Platform.select({
                        web: 'imagebutton',
                        default: 'button',
                    })}
                    accessibilityState={{ checked: item.visible }}
                    accessibilityLabel={
                        item.visible
                            ? t(ACCESSIBILITY_MY_LIST_HIDE_CHANNEL)
                            : t(ACCESSIBILITY_MY_LIST_SHOW_CHANNEL)
                    }
                >
                    {item.visible ? <EyeIcon /> : <EyeCrossedIcon />}
                </TouchableOpacity>
            </View>
        );
    },
);

export default ChannelLineupItem;
